import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import ProfessionalHeader from '../components/ProfessionalHeader';
import ProfileViewer from '../components/ProfileViewer';
import DetailedRatingModal from '../components/DetailedRatingModal_v2';
import GolfLoadingSpinner from '../components/GolfLoadingSpinner';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

interface ShaftRecommendation {
  id: number;
  brand: string;
  model: string;
  flex: string;
  weight?: number;
  launch?: string;
  spin?: string;
  torque?: number;
  material?: string;
  type?: string;
  match_reason?: string;
}

interface Equipment {
  id: number;
  brand: string;
  model: string;
  category: string;
  price?: number;
  specifications?: string;
  recommended_shafts?: ShaftRecommendation[];
  // Additional fields from backend
  loft?: number;
  shaft_flex?: string;
  shaft_model?: string;
  shaft_weight?: number;
}

interface ProfileData {
  id: number;
  [key: string]: any;
}

interface LabelingSession {
  profile: ProfileData;
  driver_options: Equipment[];
  iron_options: Equipment[];
  wedge_options: Equipment[];
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
type Category = 'driver' | 'iron' | 'wedge';

const ExpertLabelingPage: React.FC = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [session, setSession] = useState<LabelingSession | null>(null);
  const [loading, setLoading] = useState(true);
  const [usingMockData, setUsingMockData] = useState(false);

  // Detailed rating modal state
  const [showDetailedRating, setShowDetailedRating] = useState(false);

  // Selected profile fields for modal context
  const [selectedProfileFields, setSelectedProfileFields] = useState<string[]>([
    'handicap',
    'swing_speed_driver',
    'swing_speed_7iron',
    'age'
  ]);

  // Track profiles completed in this session
  const [profilesCompletedToday, setProfilesCompletedToday] = useState(0);

  // Track if there are partial ratings saved in localStorage
  const [hasPartialRatings, setHasPartialRatings] = useState(false);

  useEffect(() => {
    fetchNextProfile();
  }, []);

  // Check for partial ratings in localStorage whenever session changes
  useEffect(() => {
    if (!session?.profile?.id) {
      setHasPartialRatings(false);
      return;
    }

    const checkPartialRatings = () => {
      const categories = ['driver', 'iron', 'wedge'];
      const types = ['head', 'shaft', 'combo'];

      for (const category of categories) {
        for (const type of types) {
          const key = `expert_ratings_${type}_${session.profile.id}_${category}`;
          if (localStorage.getItem(key)) {
            setHasPartialRatings(true);
            return;
          }
        }
      }
      setHasPartialRatings(false);
    };

    checkPartialRatings();
    // Set up an interval to check periodically (every 2 seconds)
    const interval = setInterval(checkPartialRatings, 2000);
    return () => clearInterval(interval);
  }, [session?.profile?.id]);

  // Load profiles completed counter from localStorage on mount
  useEffect(() => {
    const savedData = localStorage.getItem('expert_profiles_completed');
    if (savedData) {
      try {
        const { count, date } = JSON.parse(savedData);
        const today = new Date().toDateString();

        // Reset counter if it's a new day
        if (date === today) {
          setProfilesCompletedToday(count);
        } else {
          setProfilesCompletedToday(0);
          localStorage.setItem('expert_profiles_completed', JSON.stringify({ count: 0, date: today }));
        }
      } catch (error) {
        console.error('Failed to load profiles completed counter:', error);
        setProfilesCompletedToday(0);
      }
    }
  }, []);

  // Save profiles completed counter to localStorage when it changes
  useEffect(() => {
    const today = new Date().toDateString();
    localStorage.setItem('expert_profiles_completed', JSON.stringify({
      count: profilesCompletedToday,
      date: today
    }));
  }, [profilesCompletedToday]);

  // Load selected profile fields from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem('expert_selected_profile_fields');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) {
          setSelectedProfileFields(parsed);
        }
      } catch (error) {
        console.error('Failed to load selected profile fields:', error);
      }
    }
  }, []);

  // Save selected profile fields to localStorage when changed
  useEffect(() => {
    localStorage.setItem('expert_selected_profile_fields', JSON.stringify(selectedProfileFields));
  }, [selectedProfileFields]);

  const fetchNextProfile = async () => {
    setLoading(true);
    try {
      const storageType = localStorage.getItem('auth_storage_type');
      const storage = storageType === 'session' ? sessionStorage : localStorage;
      const token = storage.getItem('access_token');
      const headers = { Authorization: `Bearer ${token}` };

      // Fetch profile
      const profileResponse = await axios.get(`${API_BASE_URL}/api/v1/expert/profiles/next?only_synthetic=false`, {
        headers
      });

      const profileData = profileResponse.data.profile;
      const profileId = profileData.id;

      // Fetch equipment options for this profile
      const [driversResponse, ironsResponse, wedgesResponse] = await Promise.all([
        axios.get(`${API_BASE_URL}/api/v1/expert/equipment/drivers?profile_id=${profileId}`, { headers }),
        axios.get(`${API_BASE_URL}/api/v1/expert/equipment/irons?profile_id=${profileId}`, { headers }),
        axios.get(`${API_BASE_URL}/api/v1/expert/equipment/wedges?profile_id=${profileId}`, { headers })
      ]);

      setSession({
        profile: profileData,
        driver_options: driversResponse.data,
        iron_options: ironsResponse.data,
        wedge_options: wedgesResponse.data
      });
      setUsingMockData(false);
    } catch (error) {
      console.error('Error fetching profile:', error);
      setUsingMockData(true);
      // Mock data for development
      setSession({
        profile: {
          id: 547,
          age: 34,
          gender: 'Male',
          handicap: 8,
          years_playing: 12,
          height_inches: 73,
          swing_speed_driver: 105,
          swing_speed_7iron: 85,
        },
        driver_options: Array.from({ length: 10 }, (_, i) => ({
          id: i + 1,
          brand: ['Ping', 'Callaway', 'TaylorMade', 'Titleist', 'Cobra'][i % 5],
          model: [`Model ${i + 1}`, 'Max', 'Speed', 'TSi2', 'F-Max'][i % 5],
          category: 'driver',
          price: 400 + i * 50,
          specifications: `${9 + (i * 0.5)}° loft, ${65 + i * 5}g shaft`
        })),
        iron_options: Array.from({ length: 10 }, (_, i) => ({
          id: 11 + i,
          brand: ['Titleist', 'Ping', 'Mizuno', 'Callaway', 'Srixon'][i % 5],
          model: [`T${200 + i * 50}`, 'i210', 'JPX', 'Apex', 'ZX7'][i % 5],
          category: 'iron',
          price: 900 + i * 100,
          specifications: `4-PW, ${120 + i * 5}g shaft, Steel`
        })),
        wedge_options: Array.from({ length: 10 }, (_, i) => ({
          id: 21 + i,
          brand: ['Titleist Vokey', 'Cleveland', 'Callaway', 'Ping', 'TaylorMade'][i % 5],
          model: ['SM9', 'RTX', 'Jaws', 'Glide', 'MG3'][i % 5],
          category: 'wedge',
          price: 150 + i * 10,
          specifications: `${52 + i * 2}° loft, ${8 + i}° bounce`
        }))
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDetailedRatingSuccess = () => {
    setShowDetailedRating(false);
    setProfilesCompletedToday(prev => prev + 1);
    // Fetch next profile after successful submission
    fetchNextProfile();
  };

  const handleSkipProfile = () => {
    // Check if there are any partial ratings saved in localStorage
    const categories = ['driver', 'iron', 'wedge'];
    const types = ['head', 'shaft', 'combo'];
    let hasPartialRatings = false;

    if (session?.profile?.id) {
      for (const category of categories) {
        for (const type of types) {
          const key = `expert_ratings_${type}_${session.profile.id}_${category}`;
          if (localStorage.getItem(key)) {
            hasPartialRatings = true;
            break;
          }
        }
        if (hasPartialRatings) break;
      }
    }

    if (hasPartialRatings) {
      const confirmed = window.confirm(
        'You have unsaved ratings for this profile. Skipping will lose this progress.\n\n' +
        'Click OK to skip anyway, or Cancel to continue rating.'
      );
      if (!confirmed) {
        return; // Don't skip if user cancels
      }
    }

    fetchNextProfile();
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <GolfLoadingSpinner message="Loading next profile..." size="large" />
      </div>
    );
  }

  if (!session) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <p className="text-xl text-gray-600">No profiles available for labeling.</p>
          <button
            onClick={() => navigate('/expert')}
            className="mt-4 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Back to Dashboard
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Professional Header */}
      <ProfessionalHeader user={user} onLogout={logout} />

      {/* Sub-Header with Profile Info */}
      <div className="bg-white shadow-md border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/expert')}
                className="text-gray-600 hover:text-gray-900 font-medium"
              >
                ← Back to Dashboard
              </button>
              <div className="border-l border-gray-300 pl-4">
                <h1 className="text-xl font-bold text-gray-900">Expert Labeling - Profile #{session.profile.id}</h1>
                <p className="text-sm text-gray-600">{session.profile.gender}, {session.profile.age} years, {session.profile.handicap} handicap</p>
              </div>
            </div>

            {/* Profiles Completed Counter */}
            <div className="px-4 py-2 rounded-lg bg-green/10 border border-green-ink">
              <div className="flex items-center space-x-2">
                <svg className="w-5 h-5 text-green-ink" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div>
                  <p className="text-xs text-gray-500 font-medium">Today's Progress</p>
                  <p className="text-lg font-bold text-green-ink">{profilesCompletedToday} Profile{profilesCompletedToday !== 1 ? 's' : ''} Completed</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content - Profile + Action Buttons */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Mock Data Warning Banner */}
        {usingMockData && (
          <div className="mb-6 bg-red-100 border-l-4 border-red-600 rounded-lg p-4 shadow-lg">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <div className="ml-3 flex-1">
                <h3 className="text-red-800 font-bold text-lg">⚠️ WARNING: Using Mock Data</h3>
                <p className="text-red-700 mt-1">
                  Backend not connected. You are viewing FAKE placeholder data. Equipment and profiles are not from the database.
                </p>
                <p className="text-red-600 text-sm mt-2">
                  To use real data: <strong>1)</strong> Login at <code className="bg-red-200 px-1 rounded">/login</code>
                  {' '}<strong>2)</strong> Ensure backend is running <strong>3)</strong> Check authentication token
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Profile Display */}
        <ProfileViewer
          profile={session.profile}
          selectedFields={selectedProfileFields}
          onFieldSelectionChange={setSelectedProfileFields}
        />

        {/* Equipment Rating Section */}
        <div className="mt-6">
          {/* Desktop Use Only Notice */}
          <div className="mb-4 bg-amber-50 border-l-4 border-amber-500 rounded-lg p-4">
            <div className="flex items-center">
              <svg className="w-5 h-5 text-amber-600 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              <p className="text-sm text-amber-800 font-medium">
                <strong>Desktop/Laptop Required:</strong> The rating system is optimized for computer use only. Please use a desktop or laptop computer for the best experience.
              </p>
            </div>
          </div>

          <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-xl p-8 shadow-2xl">
            <div className="text-center">
              <h3 className="text-3xl font-bold text-white mb-3">Equipment Rating System</h3>
              <p className="text-lg text-blue-100 mb-2">Comprehensive component-level rating for precise recommendations</p>
              <p className="text-sm text-blue-200 mb-6">
                Rate equipment at granular level: Head → Shaft → Flex → Configuration
              </p>
              <button
                onClick={() => setShowDetailedRating(true)}
                className="hidden sm:inline-block px-12 py-5 bg-white text-blue-600 font-bold text-lg rounded-lg hover:shadow-2xl transform hover:scale-105 transition-all"
              >
                🏌️ Rate Equipment for This Profile
              </button>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="mt-6">
            <button
              onClick={handleSkipProfile}
              className={`w-full px-6 py-3 rounded-lg font-semibold transition-all ${
                hasPartialRatings
                  ? 'bg-yellow-100 text-yellow-800 border-2 border-yellow-400 hover:bg-yellow-200'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
              }`}
              title={hasPartialRatings ? 'Warning: You have unsaved ratings that will be lost' : ''}
            >
              {hasPartialRatings ? '⚠️ Skip (Unsaved Progress)' : 'Skip This Profile'}
            </button>
          </div>
        </div>
      </div>

      {/* Detailed Rating Modal */}
      {showDetailedRating && session && (
        <DetailedRatingModal
          isOpen={showDetailedRating}
          profile={session.profile}
          selectedProfileFields={selectedProfileFields}
          onClose={() => setShowDetailedRating(false)}
          onSuccess={handleDetailedRatingSuccess}
        />
      )}
    </div>
  );
};

export default ExpertLabelingPage;
